import _ from 'lodash';
import qconsole from 'scripts/lib/qconsole';

import {
  addPendingRequestIdForConversation,
  clearPendingRequest,
} from 'actions/composition/lib/update_conversation_workflow';
import Attachment from 'models/attachment';
import ConversationMessage from 'scripts/domain/models/conversation_message';
import ConversationMessageAttachment from 'models/conversation_message_attachment';
import createConversationMessage from 'models/create_conversation_message';
import CreateInstagramComposition from './create_instagram_composition';
import CreateTweetComposition from 'actions/composition/create_tweet_composition';
import CreateWhatsappComposition from 'actions/composition/create_whatsapp_composition';
import Err from 'models/err';
import htmlToText from 'models/conversation_item/html_to_text';
import IdGenerator from 'scripts/domain/contracts/id_generator';
import InteractionType from 'models/interaction_type';
import { isEmpty } from 'models/composition/composition_content_shared';
import SubmitComposition, { getOutgoingItemDto } from 'actions/composition/lib/submit_composition';
import Upload from 'models/upload';

export default class SendConversationMessage extends SubmitComposition {
  sendMessage(conversationUpdates, conversationMessageItem) {
    // mark the request as pending in the conversation workflow
    const requestId = IdGenerator.newId();
    addPendingRequestIdForConversation(this.context, conversationMessageItem.conversationId, requestId);

    this.context.gateways.conversationMessage
      .add({
        id: conversationMessageItem.id,
        customerId: this.currentCustomerId,
        conversation: conversationUpdates,
        conversationItem: getOutgoingItemDto(conversationMessageItem),
      })
      .finally(() => {
        clearPendingRequest(this.context, requestId);
      });
  }

  createOutgoingItems(composition, compositionContent) {
    const plainBody = htmlToText(compositionContent.bodyHtml);
    if (isEmpty(plainBody) && composition.attachments.length === 0) {
      return compErrors({
        attr: 'bodyHtml',
        code: Err.Code.BLANK,
        detail: `Message cannot be empty`,
      });
    }

    if (plainBody.length > this.getMaxCharCountByChannel(compositionContent.channel)) {
      return compErrors({
        attr: 'bodyHtml',
        code: Err.Code.TOO_LONG,
        detail: `Your message can contain a maximum of ${this.getMaxCharCountByChannel(
          compositionContent.channel
        )} characters`,
      });
    }

    const compositionErrors = [].concat(
      composition.validateAttachmentStatus(),
      composition.validateTotalAttachmentSize(),
      composition.validateAttachmentCount()
    );
    if (compositionErrors.length) {
      return { compositionErrors };
    }

    let conversationItems = [];
    if (_.trim(plainBody)) {
      conversationItems.push(
        createConversationMessage(
          this.currentAgent,
          [],
          compositionContent,
          composition.conversationId,
          composition.customerId,
          composition.snippetIds,
          composition.relatedSnippetIds,
          composition.translation
        )
      );
    }

    let attachments = this.createConversationMessageAttachments(composition);
    if (attachments.length > 0) {
      compositionContent.bodyHtml = '';
    }

    conversationItems = conversationItems.concat(
      attachments.map(attachment =>
        createConversationMessage(
          this.currentAgent,
          [attachment],
          compositionContent,
          composition.conversationId,
          composition.customerId
        )
      )
    );

    return { compositionErrors: [], conversationItems };
  }

  getMaxCharCountByChannel(channel) {
    switch (channel) {
      case InteractionType.INSTAGRAM_DIRECT:
        return ConversationMessage.MAX_INSTAGRAM_MESSAGE_SIZE;
      case InteractionType.WHATSAPP:
        return ConversationMessage.MAX_WHATSAPP_MESSAGE_SIZE;

      default:
        return ConversationMessage.MAX_CONVERSATION_MESSAGE_SIZE;
    }
  }

  createConversationMessageAttachments(composition) {
    return composition.attachments.map(attachment => asConversationMessageAttachment(composition, attachment));
  }

  afterSend(itemContent) {
    // create a new conversation message to continue talking with the customer
    switch (itemContent.channel) {
      case InteractionType.TWITTER:
        this.context.executeAction(CreateTweetComposition, { conversationId: this.activeConversation.id });
        break;
      case InteractionType.WHATSAPP:
        this.context.executeAction(CreateWhatsappComposition, { conversationId: this.activeConversation.id });
        break;
      case InteractionType.INSTAGRAM_COMMENT:
        // For Instagram comments, the composer should not be reopened.
        break;
      case InteractionType.FB_COMMENT:
        // A reply targets one specific comment, so there is nothing to reopen against.
        break;
      case InteractionType.INSTAGRAM_DIRECT:
        // For Instagram private replies (comment-to-DM), the composer should not be reopened.
        if (!itemContent.meta?.parentCommentId) {
          this.context.executeAction(CreateInstagramComposition, { conversationId: this.activeConversation.id });
        }
        break;
      default:
        qconsole.error('invalid channel interaction type', { interactionType: itemContent.channel });
    }
  }
}

function asConversationMessageAttachment(composition, attachment) {
  let fileDescriptor = attachment.fileDescriptor();
  let source = attachment instanceof Upload ? createUploadSource(composition, attachment.id) : attachment.source;

  return ConversationMessageAttachment.create({
    id: attachment.id,
    contentType: fileDescriptor.contentType,
    filename: fileDescriptor.filename,
    fileSize: fileDescriptor.contentLength,
    source,
  });
}

function createUploadSource(composition, attachmentId) {
  return {
    type: Attachment.SourceType.UPLOAD,
    path: composition.getUploadPath(attachmentId),
  };
}

function compErrors({ attr, code, detail }) {
  return {
    compositionErrors: [
      new Err({
        attr,
        code,
        detail,
      }),
    ],
  };
}
